import type { SeverityLevel } from '@sentry/browser';
import { normalizeError } from './error';
import { getAppVersion } from './version';

type TReportProps = {
    section: string;
    level?: SeverityLevel;
    error: unknown;
    extras?: Record<string, unknown>;
    tags?: Record<string, string>;
};

export function reportToSentry({
    section,
    level = 'error',
    error,
    extras,
    tags,
}: TReportProps): void {
    if (typeof Sentry === 'undefined') {
        if (level === 'error' || level === 'fatal') {
            console.error(`Sentry not initiated [${section}]`, error);
        }
        return;
    }

    Sentry.withScope((scope) => {
        scope.setTag('section', section);
        scope.setLevel(level);

        // Normally already on the global scope (Sentry.setTag right after
        // Sentry.init in general.js, which also covers unhandled errors) —
        // repeated here so a report still carries the version if this bundle
        // runs on a page whose SDK was initialized elsewhere, or where that
        // setTag was skipped because the mirrored bundle does not export it.
        // Skipped when the page predates the server-rendered attribute.
        const appVersion = getAppVersion();
        if (appVersion) {
            scope.setTag('app.version', appVersion);
        }

        if (tags) {
            for (const [k, v] of Object.entries(tags)) {
                scope.setTag(k, v);
            }
        }

        if (extras) {
            for (const [k, v] of Object.entries(extras)) {
                scope.setExtra(k, v);
            }
        }

        Sentry.captureException(normalizeError(error));
    });
}
